import type { ResultEvent } from '../../../../../../../../types/events.ts';
import { DATETIME_FORMATS } from '../../../../../../../../types/dateTime.ts';
import type { SubstitutionMap } from './types.ts';

// Matches the editor's REPLACED_LOGO_HEIGHT (timer-dashboard) so the logo renders
// at the same footprint in the 3300x2550 capture canvas as it does on the editor canvas.
const EVENT_LOGO_MAX_HEIGHT_PX = 450;

// Fallbacks are the base finisher-certificate template's original colors, so unbranded
// events (or events with branding disabled) render exactly as before once the
// %PRIMARY_COLOR% / %PRIMARY_TEXT_COLOR% tokens are substituted. These tokens only exist
// in the default (unedited) template; customized certificates keep their baked-in colors.
const DEFAULT_PRIMARY_COLOR = 'rgba(214,231,255,1)';
const DEFAULT_PRIMARY_TEXT_COLOR = 'rgba(0,22,49,1)';

// Event branding colors are stored as hex, sometimes without a leading '#'
// (mirrors normalizeColor in the timer-dashboard editor).
const normalizeBrandingColor = (color?: string): string | undefined => {
  if (!color || color === 'transparent') {
    return undefined;
  }

  return color.startsWith('#') ? color : `#${color}`;
};

type BuildEventSubstitutionsParams = {
  event: ResultEvent;
  locale: string;
};

const buildEventSubstitutions = ({
  event,
  locale,
}: BuildEventSubstitutionsParams): SubstitutionMap => {
  const brandingColors = event.applyEventBranding ? event.styles.colors : undefined;

  return {
    '%EVENT_NAME%': event.name ?? '',
    '%EVENT_DATE%': event?.dates?.eventDate
      ? new Intl.DateTimeFormat(locale, DATETIME_FORMATS.date).format(
          new Date(event.dates.eventDate)
        )
      : '',
    '%EVENT_LOGO%': event.styles.logo
      ? `<img src="${event.styles.logo}" alt="${event.name} logo" style="max-height: ${EVENT_LOGO_MAX_HEIGHT_PX}px; max-width: 100%; width: auto;" />`
      : '',
    '%PRIMARY_COLOR%': normalizeBrandingColor(brandingColors?.primary) ?? DEFAULT_PRIMARY_COLOR,
    '%PRIMARY_TEXT_COLOR%':
      normalizeBrandingColor(brandingColors?.primaryFont) ?? DEFAULT_PRIMARY_TEXT_COLOR,
  };
};

export default buildEventSubstitutions;
